/**
 * Pecas de pagina logada: cabecalho, blocos, fatos, vazio, a faixa do ambiente de testes e texto copiavel.
 * Nenhuma sabe a marca nem o dominio; o app monta a tela com elas.
 */
import { Children, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { IconeCheck, IconeCopiar } from "../icones";
import { Botao } from "./Botao";
import { Dialogo } from "./Dialogo";

type Espaco = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/** Coluna com espaco fixo entre os filhos (token --e-N). */
export function Pilha({ espaco = 5, children, className }: { espaco?: Espaco; children: ReactNode; className?: string }) {
  return <div className={`mtc-pilha${className ? ` ${className}` : ""}`} style={{ gap: `var(--e-${espaco})` }}>{children}</div>;
}

/** Linha que quebra quando falta espaco. `entre` joga o primeiro e o ultimo pras pontas. */
export function Linha({ espaco = 3, entre, children }: { espaco?: Espaco; entre?: boolean; children: ReactNode }) {
  return <div className={`mtc-linha${entre ? " mtc-linha--entre" : ""}`} style={{ gap: `var(--e-${espaco})` }}>{children}</div>;
}

/** Grade de cartoes que vira uma coluna no celular. */
export function Grade({ minimo = 300, children }: { minimo?: number; children: ReactNode }) {
  return <div className="mtc-grade" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${minimo}px), 1fr))` }}>{children}</div>;
}

/** Topo de cada pagina: titulo, descricao e as acoes da pagina. Sem rotulo pequeno em cima do titulo (Vitor, 17/10: sai de todas as telas). */
export function CabecalhoDePagina({ titulo, descricao, acoes, voltar, abas }: { titulo: ReactNode; descricao?: ReactNode; acoes?: ReactNode; voltar?: ReactNode; abas?: ReactNode }) {
  return (
    <header className="mtc-cabecalho">
      <div className="mtc-cabecalho__textos">
        {voltar}
        <h1 className="mtc-titulo mtc-cabecalho__titulo">{titulo}</h1>
        {descricao && <p className="mtc-cabecalho__descricao">{descricao}</p>}
      </div>
      {acoes && <div className="mtc-cabecalho__acoes">{acoes}</div>}
      {/* Abas do assunto (uma por endereco): ficam coladas no cabecalho, antes do conteudo. */}
      {abas && <div className="mtc-cabecalho__abas">{abas}</div>}
    </header>
  );
}

/** Cartao com titulo e acoes no topo. */
export function Secao({ titulo, rotulo, acoes, children, destaque }: { titulo?: ReactNode; rotulo?: string; acoes?: ReactNode; children: ReactNode; destaque?: boolean }) {
  return (
    <section className={`mtc-cartao mtc-secao${destaque ? " mtc-secao--destaque" : ""}`}>
      {(titulo || rotulo || acoes) && (
        <div className="mtc-secao__cabeca">
          <div className="mtc-secao__textos">
            {rotulo && <span className="mtc-rotulo">{rotulo}</span>}
            {titulo && <h2 className="mtc-secao__titulo">{titulo}</h2>}
          </div>
          {acoes && <div className="mtc-secao__acoes">{acoes}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export type Fato = { rotulo: string; valor: ReactNode; detalhe?: ReactNode; mono?: boolean };

/** Pares rotulo e valor (subconta, mesa, prazo). Vira coluna unica no celular. */
export function Fatos({ itens }: { itens: Fato[] }) {
  return (
    <dl className="mtc-fatos">
      {itens.map((f) => (
        <div key={f.rotulo}>
          <dt>{f.rotulo}</dt>
          <dd className={f.mono ? "mtc-mono" : undefined}><span>{f.valor}</span>{f.detalhe && <small>{f.detalhe}</small>}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Lista sem itens: diz o que falta e, se houver, o que fazer. */
export function Vazio({ titulo, children, acao }: { titulo: string; children?: ReactNode; acao?: ReactNode }) {
  return (
    <div className="mtc-vazio">
      <strong>{titulo}</strong>
      {children && <p>{children}</p>}
      {acao}
    </div>
  );
}

/** Faixa no topo de toda tela quando o ambiente e de testes. O app decide quando mostrar. */
/**
 * O topo das faixas (ambiente de testes, versao nova): gruda no topo e nao sai ao rolar (Vitor, 17/10: "essa tarja deve
 * ficar fixa no topo, e nao desaparecer"). A altura vai pra `--altura-faixas`, que a lateral do painel, a da area e as
 * colunas grudadas descontam: a faixa nunca cobre o logo nem o menu (antes ela passava por cima do topo da lateral).
 */
export function Faixas({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const tem = Children.toArray(children).length > 0;
  useLayoutEffect(() => {
    const raiz = document.documentElement;
    const el = ref.current;
    if (!el) return;
    const medir = () => raiz.style.setProperty("--altura-faixas", `${el.offsetHeight}px`);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => {
      observador.disconnect();
      raiz.style.removeProperty("--altura-faixas");
    };
  }, [tem]);
  return tem ? <div className="mtc-faixas" ref={ref}>{children}</div> : null;
}

export function FaixaAmbiente({ titulo = "Ambiente de testes", children = "Compras, contas e e-mails daqui são simulados. Nada vale de verdade." }: { titulo?: string; children?: ReactNode }) {
  return (
    <div className="mtc-faixa mtc-faixa--ambiente" role="note" aria-label={titulo}>
      <strong>{titulo}</strong>
      <span>{children}</span>
    </div>
  );
}

/**
 * Saiu versao nova enquanto a aba estava aberta (Eduarda, 17/09: a tela antiga escondia campos que a API nova ja pedia,
 * e o erro so aparecia no fim). Fica no topo ate a pessoa atualizar; nada some do ar sozinho.
 */
export function FaixaVersaoNova({ aoAtualizar }: { aoAtualizar: () => void }) {
  return (
    <div className="mtc-faixa mtc-faixa--versao" role="alert" aria-label="Versão nova">
      <strong>Versão nova</strong>
      <span>Esta aba está com uma versão antiga da tela. Atualize pra ver os campos novos.</span>
      <button type="button" className="mtc-link" onClick={aoAtualizar}>Atualizar agora</button>
    </div>
  );
}

/**
 * Sessao assistida (spec 012): a equipe esta vendo a area como o trader ve. Fica no topo, o tempo todo, e diz de quem
 * e a area, quem esta olhando e ate quando. Nada nesta sessao escreve.
 */
export function FaixaAssistida({ trader, operador, expiraEm, aoEncerrar }: { trader: string; operador: string; expiraEm?: string; aoEncerrar?: () => void }) {
  const ate = expiraEm ? new Date(expiraEm) : null;
  const minutos = ate ? Math.max(0, Math.round((ate.getTime() - Date.now()) / 60_000)) : null;
  return (
    <div className="mtc-faixa mtc-faixa--assistida" role="note" aria-label="Sessão assistida">
      <strong>Sessão assistida</strong>
      <span>Você está vendo a área de {trader} como {operador}. Somente leitura{minutos !== null ? `, por mais ${minutos} min` : ""}.</span>
      {aoEncerrar && <button type="button" className="mtc-link" onClick={aoEncerrar}>Encerrar</button>}
    </div>
  );
}

/**
 * Botao que copia sem mostrar o valor (o link de uma linha de tabela). Copiou: "Copiado" por 2 s, anunciado ao leitor de
 * tela. O navegador negou: "Não copiou" e abre o valor pra copiar a mao, pra ninguem colar o que ja estava na area de
 * transferencia. `rotuloAcessivel` diz de qual linha (comeca pelo texto visivel: "Copiar link do Exame Start").
 */
export function BotaoCopiar({ valor, rotulo = "Copiar", rotuloAcessivel, estadoInicial = "normal" }: { valor: string; rotulo?: string; rotuloAcessivel?: string; estadoInicial?: "normal" | "copiado" | "falhou" }) {
  const [estado, setEstado] = useState(estadoInicial);
  const [aberto, setAberto] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(valor);
      setEstado("copiado");
      setTimeout(() => setEstado("normal"), 2000);
    } catch {
      setEstado("falhou");
      setAberto(true);
    }
  };
  const texto = estado === "copiado" ? "Copiado" : estado === "falhou" ? "Não copiou" : rotulo;
  return (
    <>
      <Botao variante="secundario" tamanho="pequeno" aria-label={estado === "normal" ? rotuloAcessivel : undefined} icone={estado === "copiado" ? <IconeCheck tamanho={14} /> : <IconeCopiar tamanho={14} />} onClick={() => void copiar()}>{texto}</Botao>
      <span className="mtc-oculto" aria-live="polite">{estado === "normal" ? "" : texto}</span>
      <Dialogo aberto={aberto} aoFechar={() => { setAberto(false); setEstado("normal"); }} titulo="Copie à mão" descricao="O navegador não deixou copiar sozinho. Selecione o texto e copie.">
        <TextoCopiavel valor={valor} rotulo={rotulo} />
      </Dialogo>
    </>
  );
}

/** Valor com botao de copiar (link de primeiro acesso, CPF de teste). */
export function TextoCopiavel({ valor, rotulo = "Copiar" }: { valor: string; rotulo?: string }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      setCopiado(false);
    }
  };
  return (
    <div className="mtc-copiavel">
      <code className="mtc-copiavel__valor">{valor}</code>
      <Botao type="button" variante="secundario" tamanho="pequeno" icone={copiado ? <IconeCheck tamanho={14} /> : <IconeCopiar tamanho={14} />} onClick={() => void copiar()}>{copiado ? "Copiado" : rotulo}</Botao>
    </div>
  );
}

/**
 * Botao de icone que copia um valor (subconta, mesa): confirma com o check e avisa o leitor de tela. `classe` ajusta o
 * lugar (no cartao do plano da casca ele herda a cor do cartao).
 */
export function CopiarValor({ valor, rotulo, classe }: { valor: string; rotulo: string; classe?: string }) {
  const [estado, setEstado] = useState<"normal" | "copiado" | "falhou">("normal");
  const relogio = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(relogio.current), []);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(valor);
      setEstado("copiado");
    } catch {
      setEstado("falhou");
    }
    clearTimeout(relogio.current);
    relogio.current = setTimeout(() => setEstado("normal"), 2500);
  };
  return (
    <>
      <button type="button" className={`mtc-botao-icone${classe ? ` ${classe}` : ""}`} aria-label={rotulo} title={rotulo} onClick={() => void copiar()}>
        {estado === "copiado" ? <IconeCheck tamanho={18} /> : <IconeCopiar tamanho={18} />}
      </button>
      <span className="mtc-so-leitor" role="status">{estado === "copiado" ? "Copiado" : estado === "falhou" ? "Não copiou. Selecione o número e copie." : ""}</span>
    </>
  );
}
